import React, { useState } from 'react';
import { 
  ArrowRight, 
  Calendar,
  MapPin,
  ChevronLeft,
  Check,
  Clock,
} from 'lucide-react';

const BoplyLogo = ({ size = 64 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <div className="absolute bg-purple-500 rounded-[4px]" style={{ width: size * 0.55, height: size * 0.55, top: 0, left: 0, opacity: 0.5 }} />
    <div className="absolute bg-purple-500 rounded-[4px]" style={{ width: size * 0.55, height: size * 0.55, top: size * 0.22, left: size * 0.22, opacity: 0.75 }} />
    <div className="absolute bg-blue-500 rounded-[4px]" style={{ width: size * 0.55, height: size * 0.55, top: size * 0.45, left: size * 0.45 }} />
  </div>
);

const CreateBandModal = ({ onComplete, onBack, isOpen }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasCalledComplete, setHasCalledComplete] = useState(false);
  
  const [bandName, setBandName] = useState('');
  const [showData, setShowData] = useState({
    venue: '',
    city: '',
    date: '',
    showType: 'upcoming',
  });
  const [createdShow, setCreatedShow] = useState(null);

  if (!isOpen) return null;

  const today = new Date().toISOString().split('T')[0];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // =====================================================
  // Called when user clicks "Done" on the show form
  // or "Skip for now" — advances UI only, does NOT
  // call onComplete yet to avoid double-firing
  // =====================================================
  const handleShowStepDone = async (skipShow = false) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const firstShow = skipShow ? null : (showData.venue && showData.date ? {
      id: Date.now().toString(),
      venue: showData.venue,
      city: showData.city || '',
      date: showData.date,
      guarantee: 0,
      status: showData.showType === 'past' ? 'completed' : 'confirmed',
      eventType: 'show',
      createdAt: new Date().toISOString(),
    } : null);

    await new Promise(resolve => setTimeout(resolve, 400));
    setIsSubmitting(false);

    if (firstShow) {
      // Show success screen first
      setCreatedShow(firstShow);
      setStep(3);
    } else {
      // No show — go straight to dashboard
      handleFinalComplete(null);
    }
  };

  // =====================================================
  // The ONE place onComplete is called — prevents double-fire
  // =====================================================
  const handleFinalComplete = (firstShow) => {
    if (hasCalledComplete) return;
    setHasCalledComplete(true);
    onComplete({
      bandName: bandName.trim(),
      firstShow: firstShow || null,
    });
  };

  // =====================================================
  // STEP 1: Band Name
  // =====================================================
  if (step === 1) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
          
          <div className="flex gap-2 p-4 pb-0">
            <div className="flex-1 h-1.5 bg-purple-500 rounded-full" />
            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full" />
          </div>

          <div className="p-6 pb-4 text-center">
            <div className="inline-flex items-center justify-center mb-4">
              <BoplyLogo size={64} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              What's your band called?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              This is how you'll identify your group in Boply
            </p>
          </div>

          <div className="px-6 pb-6">
            <input
              type="text"
              value={bandName}
              onChange={(e) => setBandName(e.target.value)}
              placeholder="Enter band or artist name..."
              autoFocus
              className="w-full px-4 py-4 text-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all"
              onKeyDown={(e) => { if (e.key === 'Enter' && bandName.trim()) setStep(2); }}
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
              Solo artist? Just use your name or stage name
            </p>

            <div className="flex gap-3 mt-6">
              {onBack && (
                <button onClick={onBack} className="px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl font-medium transition-colors">
                  <ChevronLeft size={20} />
                </button>
              )}
              <button
                onClick={() => setStep(2)}
                disabled={!bandName.trim()}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition-all duration-200 ${
                  bandName.trim()
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/25 hover:scale-[1.02] active:scale-[0.98]'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes scale-in { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          .animate-scale-in { animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        `}</style>
      </div>
    );
  }

  // =====================================================
  // STEP 2: First Show (Optional)
  // =====================================================
  if (step === 2) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
          
          <div className="flex gap-2 p-4 pb-0">
            <div className="flex-1 h-1.5 bg-purple-500 rounded-full" />
            <div className="flex-1 h-1.5 bg-purple-500 rounded-full" />
          </div>

          <div className="p-6 pb-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl mb-4 shadow-lg">
              <Calendar size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Add your first show?
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Just 2 fields — you can add more details later
            </p>
          </div>

          <div className="px-6 pb-6 space-y-4">
            
            {/* Show Type Toggle */}
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-xl p-1 gap-1">
              <button type="button" onClick={() => setShowData({ ...showData, showType: 'upcoming' })}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${showData.showType === 'upcoming' ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                📅 Upcoming
              </button>
              <button type="button" onClick={() => setShowData({ ...showData, showType: 'past' })}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${showData.showType === 'past' ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                ✅ Past Show
              </button>
            </div>

            {showData.showType === 'past' && (
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg">
                  <Clock size={16} className="text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Add your last gig to start building your history
                </p>
              </div>
            )}

            {/* Venue */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Venue <span className="text-gray-400 font-normal">required</span>
              </label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={showData.venue}
                  onChange={(e) => setShowData({ ...showData, venue: e.target.value })}
                  placeholder="e.g., The Blue Room"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all"
                />
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                Date <span className="text-gray-400 font-normal">required</span>
              </label>
              <input
                type="date"
                value={showData.date}
                onChange={(e) => setShowData({ ...showData, date: e.target.value })}
                max={showData.showType === 'past' ? today : undefined}
                min={showData.showType === 'upcoming' ? today : undefined}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all"
              />
            </div>

            {/* City (Optional) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                City <span className="text-gray-400 font-normal">optional</span>
              </label>
              <input
                type="text"
                value={showData.city}
                onChange={(e) => setShowData({ ...showData, city: e.target.value })}
                placeholder="e.g., Austin"
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button onClick={() => setStep(1)} className="px-4 py-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl font-medium transition-colors">
                <ChevronLeft size={20} />
              </button>

              <button
                onClick={() => handleShowStepDone(true)}
                disabled={isSubmitting}
                className="flex-1 py-3.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                Skip for now
              </button>

              <button
                onClick={() => handleShowStepDone(false)}
                disabled={isSubmitting || !showData.venue || !showData.date}
                className={`flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition-all duration-200 ${
                  showData.venue && showData.date && !isSubmitting
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-400 hover:to-emerald-400 shadow-lg shadow-green-500/25 hover:scale-[1.02] active:scale-[0.98]'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Check size={18} /> Done</>
                )}
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes scale-in { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          .animate-scale-in { animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        `}</style>
      </div>
    );
  }

  // =====================================================
  // STEP 3: Success — calls onComplete exactly ONCE
  // =====================================================
  if (step === 3 && createdShow) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 animate-scale-in">
          
          <div className="relative bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 p-8 text-center overflow-hidden">
            <div className="absolute top-4 left-4 w-8 h-8 bg-yellow-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="absolute top-8 right-8 w-6 h-6 bg-pink-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="absolute bottom-4 left-12 w-5 h-5 bg-green-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.4s' }} />
            <div className="absolute bottom-6 right-16 w-7 h-7 bg-orange-400 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="relative">
              <div className="text-6xl mb-3">🎉</div>
              <h2 className="text-2xl font-bold text-white mb-1">You're All Set!</h2>
              <p className="text-purple-100 text-sm">
                <span className="font-semibold">{bandName}</span> is ready to rock
              </p>
            </div>
          </div>

          <div className="p-6">
            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl">
              <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 dark:text-purple-400 mb-2">
                <Calendar size={14} />
                YOUR FIRST SHOW
              </div>
              <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{createdShow.venue}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span>{formatDate(createdShow.date)}</span>
                {createdShow.city && <><span>•</span><span>{createdShow.city}</span></>}
              </div>
            </div>

            <div className="flex items-start gap-3 mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <div className="text-xl">💡</div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Tap your show anytime to add pay, times, contacts, and notes!
              </p>
            </div>

            {/* ✅ This is the ONLY place onComplete fires */}
            <button
              onClick={() => handleFinalComplete(createdShow)}
              className="w-full flex items-center justify-center gap-2 mt-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Let's Go!
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
        <style>{`
          @keyframes scale-in { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          .animate-scale-in { animation: scale-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
          @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
          .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        `}</style>
      </div>
    );
  }

  return null;
};

export default CreateBandModal;